import React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import logo from "./../assets/logo/Pontis Global Logo - Horizontal Reversed.svg";
import { clearSessionCache } from "../services/userService";

const LoginPage: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(false);

  const isIntentionalLogout = sessionStorage.getItem('pontis_intentional_logout') === 'true';
  const fromPath = isIntentionalLogout? "/dashboard": (location.state as { from?: string } | null)?.from ?? "/dashboard";

  const activeAccount = instance.getActiveAccount();
  const storedHomeAccountId = localStorage.getItem("pontis_active_account_home_id");
  const resolvedAccount  = activeAccount ? (storedHomeAccountId ? accounts.find((account) => account.homeAccountId === storedHomeAccountId) : undefined) ??(accounts.length === 1 ? accounts[0] : undefined) : undefined;

  // Redirect if already authenticated
 React.useEffect(() => {
  if (isAuthenticated && resolvedAccount) {
    sessionStorage.removeItem('pontis_intentional_logout');
    instance.setActiveAccount(resolvedAccount);
    
    // Check if account has changed 
    const previousAccountId = localStorage.getItem("pontis_active_account_home_id");
    const accountSwitched = previousAccountId && previousAccountId !== resolvedAccount.homeAccountId;
    
    localStorage.setItem("pontis_active_account_home_id", resolvedAccount.homeAccountId);
    
    if (accountSwitched) {
      localStorage.removeItem('pontis_selected_tenant_id');
      localStorage.removeItem('pontis_selected_tenant_role');
    }
    
    clearSessionCache();
    navigate("/auth-loading", { replace: true, state: { from: fromPath } });
  }
}, [fromPath, instance, isAuthenticated, navigate, resolvedAccount]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      
      const loginResult = await instance.loginPopup({
        scopes: [process.env.REACT_APP_MSAL_API_SCOPE || ""],
        prompt: "select_account"
      });

      if (loginResult.account) {
        instance.setActiveAccount(loginResult.account);
        localStorage.setItem("pontis_active_account_home_id", loginResult.account.homeAccountId);
      }
      
    } catch (error: any) {
      if (error.errorCode !== "user_cancelled") {
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render login form if already authenticated (prevents flash)
  if (isAuthenticated && resolvedAccount) {
    return (
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "#000"
      }}>
        <CircularProgress sx={{ color: 'hsl(19 101% 51% / 1)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      bgcolor: "#000",
      color: "#fff"
    }}>
      <Box sx={{ mb: 3, height: 64, display: 'flex', alignItems: 'center' }}>
        <img src={logo} className="app-logo" alt="Pontis DMS" style={{ transform: 'scale(4)' }} />
      </Box>
      
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#fff' }}>
          Sign in to Document Management System
        </Typography>
      </Box>

      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#fff' }}>
          Sign in to DMS
        </Typography>
      </Box>
      
      <Button
        variant="contained"
        onClick={handleLogin}
        disabled={isLoading}
        sx={{
          background: 'hsl(19 101% 51% / 1)',
          color: '#fff',
          fontWeight: 600,
          borderRadius: 2,
          px: 4,
          py: 1.5,
          '&:hover': {
            background: 'hsl(19 101% 41% / 1)'
          },
          '&:disabled': {
            background: 'hsl(19 101% 31% / 1)'
          }
        }}
      >
        {isLoading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
            Signing in...
          </>
        ) : (
          'Login with Microsoft'
        )}
      </Button>
    </Box>
  );
};

export default LoginPage;
